import i18n from 'i18n'

type NativeControl = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement

type FieldCandidate = {
 control: HTMLElement
 root: HTMLElement
 label?: HTMLLabelElement | null
 virtualRequired?: boolean
}

const FIELD_ATTR = 'data-native-invalid-field'
const MESSAGE_CLASS = 'native-validation-feedback'
const INVALID_CLASS = 'native-invalid-control'
const REQUIRED_STAR_CLASS = 'app-required-star'
const REQUIRED_STAR_ATTR = 'data-required-star'
const ATTEMPTED_ATTR = 'data-validation-attempted'

const ACTION_TEXT_RE = /(حفظ|إنشاء|انشاء|تحديث|تسجيل|تنفيذ|اعتماد|تطبيق|Save|Create|Update|Submit|Apply|Confirm)/i
const PASSIVE_TEXT_RE = /(إلغاء|الغاء|إغلاق|اغلاق|حذف|السابق|التالي|طباعة|بحث|Cancel|Close|Delete|Print|Search|Next|Previous)/i

function isNativeControl(el: Element | null): el is NativeControl {
 return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement
}

function isDisabled(el: HTMLElement): boolean {
 return (
 (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || el instanceof HTMLButtonElement)
 && el.disabled
 )
}

function isVisible(el: HTMLElement): boolean {
 if (el.hidden || el.getAttribute('aria-hidden') === 'true') return false
 const style = window.getComputedStyle(el)
 if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false
 return el.getClientRects().length > 0
}

function isSkippableNativeControl(control: NativeControl): boolean {
 if (isDisabled(control)) return true
 if (control instanceof HTMLInputElement) {
 const type = control.type.toLowerCase()
 return ['hidden', 'button', 'submit', 'reset', 'file', 'image'].includes(type)
 }
 return false
}

function cleanLabelText(label?: HTMLLabelElement | null): string {
 const raw = label?.textContent
 ?.replace(/\*/g, '')
 .replace(/مطلوب/g, '')
 .replace(/Required/gi, '')
 .replace(/\s+/g, ' ')
 .trim()
 return raw || i18n.t('form.validation.native.fieldFallback')
}

function makeRequiredStar(): HTMLSpanElement {
 const star = document.createElement('span')
 star.className = `${REQUIRED_STAR_CLASS} text-destructive`
 star.setAttribute(REQUIRED_STAR_ATTR, 'true')
 star.setAttribute('aria-hidden', 'true')
 star.textContent = '*'
 return star
}

function labelHasRequiredMarker(label: HTMLLabelElement): boolean {
 if (label.querySelector(`[${REQUIRED_STAR_ATTR}="true"], .${REQUIRED_STAR_CLASS}`)) return true
 return (label.textContent ?? '').includes('*')
}

function decorateRequiredLabel(label: HTMLLabelElement, ensureMarker = false): void {
 let hasMarker = false

 label.querySelectorAll('span').forEach((span) => {
 if ((span.textContent ?? '').trim() === '*') {
 span.classList.add(REQUIRED_STAR_CLASS, 'text-destructive')
 span.setAttribute(REQUIRED_STAR_ATTR, 'true')
 span.setAttribute('aria-hidden', 'true')
 hasMarker = true
 }
 })

 const textNodes: Text[] = []
 const walker = document.createTreeWalker(label, NodeFilter.SHOW_TEXT, {
 acceptNode(node) {
 const parent = node.parentElement
 if (!parent || parent.closest(`[${REQUIRED_STAR_ATTR}="true"], .${REQUIRED_STAR_CLASS}`)) {
 return NodeFilter.FILTER_REJECT
 }
 return node.textContent?.includes('*') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
 },
 })

 while (walker.nextNode()) {
 textNodes.push(walker.currentNode as Text)
 }

 textNodes.forEach((node) => {
 const parts = (node.textContent ?? '').split('*')
 const fragment = document.createDocumentFragment()
 parts.forEach((part, index) => {
 if (part) fragment.appendChild(document.createTextNode(part))
 if (index < parts.length - 1) {
 fragment.appendChild(makeRequiredStar())
 hasMarker = true
 }
 })
 node.parentNode?.replaceChild(fragment, node)
 })

 if (ensureMarker && !hasMarker && !labelHasRequiredMarker(label)) {
 label.appendChild(document.createTextNode(' '))
 label.appendChild(makeRequiredStar())
 const sr = document.createElement('span')
 sr.className = 'sr-only'
 sr.textContent = i18n.t('form.required')
 label.appendChild(sr)
 }
}

function nearestFieldRoot(el: HTMLElement): HTMLElement {
 return (
 el.closest<HTMLElement>('[data-validation-field], .mb-3, .space-y-1, .space-y-2, .space-y-3, .form-group')
 || el.parentElement
 || el
 )
}

function labelForControl(control: HTMLElement, scope: ParentNode = document): HTMLLabelElement | null {
 if (isNativeControl(control)) {
 const nativeLabel = control.labels?.[0]
 if (nativeLabel) return nativeLabel
 }
 const id = control.id
 if (id) {
 const escapedId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
 ? CSS.escape(id)
 : id.replace(/"/g, '\\"')
 const label = scope.querySelector<HTMLLabelElement>(`label[for="${escapedId}"]`)
 if (label) return label
 }
 return nearestFieldRoot(control).querySelector('label')
}

function firstVisibleControl(root: ParentNode): HTMLElement | null {
 const controls = Array.from(
 root.querySelectorAll<HTMLElement>('input:not([type="hidden"]), textarea, select, [role="combobox"]')
 )
 return controls.find((control) => !isDisabled(control) && isVisible(control)) ?? null
}

function controlForLabel(label: HTMLLabelElement, scope: ParentNode): HTMLElement | null {
 if (label.htmlFor) {
 const byId = document.getElementById(label.htmlFor)
 if (byId instanceof HTMLElement && (scope === document || (scope as Element).contains(byId)) && isVisible(byId)) {
 return byId
 }
 }
 return firstVisibleControl(nearestFieldRoot(label))
}

function candidateKey(candidate: FieldCandidate): string {
 return candidate.control.id || candidate.control.getAttribute('name') || `${candidate.root.textContent}:${candidate.control.tagName}`
}

function getRequiredCandidates(scope: ParentNode): FieldCandidate[] {
 const byKey = new Map<string, FieldCandidate>()
 const nativeControls = Array.from(scope.querySelectorAll<NativeControl>('input, textarea, select'))

 nativeControls.forEach((control) => {
 if (isSkippableNativeControl(control) || !isVisible(control)) return
 const required = control.required || control.getAttribute('aria-required') === 'true'
 if (!required) return
 const candidate: FieldCandidate = {
 control,
 root: nearestFieldRoot(control),
 label: labelForControl(control, scope),
 }
 byKey.set(candidateKey(candidate), candidate)
 })

 Array.from(scope.querySelectorAll<HTMLLabelElement>('label')).forEach((label) => {
 if (!isVisible(label) || !labelHasRequiredMarker(label)) return
 const control = controlForLabel(label, scope)
 if (!control) return
 const candidate: FieldCandidate = {
 control,
 root: nearestFieldRoot(label),
 label,
 virtualRequired: true,
 }
 byKey.set(candidateKey(candidate), candidate)
 })

 return Array.from(byKey.values())
}

function hasValue(candidate: FieldCandidate): boolean {
 const control = candidate.control
 if (isNativeControl(control)) {
 if (control instanceof HTMLInputElement && (control.type === 'checkbox' || control.type === 'radio')) {
 return control.checked
 }
 return String(control.value ?? '').trim() !== ''
 }

 if (control.getAttribute('role') === 'combobox') {
 return !control.hasAttribute('data-placeholder')
 }

 return String(control.textContent ?? '').trim() !== ''
}

function nativeValidationMessage(control: NativeControl, label?: HTMLLabelElement | null): string {
 const name = cleanLabelText(label)
 if (control.validity.valueMissing) {
 return control instanceof HTMLSelectElement
  ? i18n.t('form.validation.native.selectField', { name })
  : i18n.t('form.validation.native.enterField', { name })
 }
 if (control.validity.typeMismatch) return i18n.t('form.validation.native.typeMismatch', { name })
 if (control.validity.rangeUnderflow) return i18n.t('form.validation.native.rangeUnderflow', { name })
 if (control.validity.rangeOverflow) return i18n.t('form.validation.native.rangeOverflow', { name })
 if (control.validity.stepMismatch || control.validity.badInput || control.validity.patternMismatch) {
 return i18n.t('form.validation.native.invalidValue', { name })
 }
 return control.validationMessage || i18n.t('form.validation.native.reviewField', { name })
}

function candidateMessage(candidate: FieldCandidate): string {
 const label = candidate.label
 if (isNativeControl(candidate.control) && !candidate.control.validity.valid) {
 return nativeValidationMessage(candidate.control, label)
 }
 const name = cleanLabelText(label)
 return candidate.control.getAttribute('role') === 'combobox'
  ? i18n.t('form.validation.native.selectField', { name })
  : i18n.t('form.validation.native.enterField', { name })
}

function ensureMessage(root: HTMLElement): HTMLElement {
 let message = root.querySelector<HTMLElement>(`:scope > .${MESSAGE_CLASS}, .${MESSAGE_CLASS}`)
 if (!message) {
 message = document.createElement('p')
 message.className = MESSAGE_CLASS
 message.setAttribute('role', 'alert')
 message.setAttribute('aria-live', 'polite')
 root.appendChild(message)
 }
 return message
}

function markInvalid(candidate: FieldCandidate): void {
 candidate.root.setAttribute(FIELD_ATTR, 'true')
 candidate.control.classList.add(INVALID_CLASS)
 candidate.control.setAttribute('aria-invalid', 'true')
 ensureMessage(candidate.root).textContent = candidateMessage(candidate)
 if (candidate.label) decorateRequiredLabel(candidate.label, true)
}

function clearInvalid(candidate: FieldCandidate): void {
 candidate.root.removeAttribute(FIELD_ATTR)
 candidate.control.classList.remove(INVALID_CLASS)
 if (candidate.control.getAttribute('aria-invalid') === 'true') {
 candidate.control.removeAttribute('aria-invalid')
 }
 const message = candidate.root.querySelector<HTMLElement>(`.${MESSAGE_CLASS}`)
 if (message) message.remove()
}

function isInvalid(candidate: FieldCandidate): boolean {
 if (isNativeControl(candidate.control) && !candidate.control.validity.valid) return true
 return candidate.virtualRequired === true && !hasValue(candidate)
}

function validateScope(scope: ParentNode): FieldCandidate[] {
 const invalid: FieldCandidate[] = []
 getRequiredCandidates(scope).forEach((candidate) => {
 if (isInvalid(candidate)) {
 markInvalid(candidate)
 invalid.push(candidate)
 } else {
 clearInvalid(candidate)
 }
 })
 return invalid
}

function focusFirstInvalid(invalid: FieldCandidate[]): void {
 const first = invalid[0]
 if (!first) return
 first.root.scrollIntoView({ block: 'center', behavior: 'smooth' })
 window.setTimeout(() => {
 try {
 first.control.focus({ preventScroll: true })
 } catch {
 first.control.focus()
 }
 }, 120)
}

function stopValidationEvent(event: Event): void {
 event.preventDefault()
 event.stopPropagation()
 event.stopImmediatePropagation()
}

function validationScopeForButton(button: HTMLElement): ParentNode | null {
 if (button instanceof HTMLButtonElement && button.form) return button.form
 const form = button.closest('form')
 if (form) return form
 return button.closest('[role="dialog"], [data-validation-scope], main, section')
}

function isValidationAction(button: HTMLElement): boolean {
 if (button instanceof HTMLButtonElement && button.disabled) return false
 if (button instanceof HTMLInputElement && button.disabled) return false
 if (button instanceof HTMLButtonElement && button.type === 'submit') return true
 if (button instanceof HTMLInputElement && button.type === 'submit') return true

 const text = (button.textContent ?? button.getAttribute('aria-label') ?? '').replace(/\s+/g, ' ').trim()
 if (!text || PASSIVE_TEXT_RE.test(text)) return false
 return ACTION_TEXT_RE.test(text)
}

function markScopeAttempted(scope: ParentNode): void {
 if (scope instanceof HTMLElement) {
 scope.setAttribute(ATTEMPTED_ATTR, 'true')
 }
}

function decorateRequiredLabels(scope: ParentNode): void {
 getRequiredCandidates(scope).forEach((candidate) => {
 if (candidate.label) decorateRequiredLabel(candidate.label, true)
 })
 Array.from(scope.querySelectorAll<HTMLLabelElement>('label')).forEach((label) => {
 if (labelHasRequiredMarker(label)) decorateRequiredLabel(label)
 })
}

function refreshInvalidFields(scope: ParentNode = document): void {
 Array.from(document.querySelectorAll<HTMLElement>(`[${FIELD_ATTR}="true"]`)).forEach((root) => {
 if (scope !== document && scope instanceof Element && !scope.contains(root)) return
 const control = firstVisibleControl(root)
 if (!control) return
 const candidate: FieldCandidate = {
 control,
 root,
 label: root.querySelector('label'),
 virtualRequired: true,
 }
 if (isInvalid(candidate)) {
 markInvalid(candidate)
 } else {
 clearInvalid(candidate)
 }
 })
}

export function installNativeFormValidationFeedback(): () => void {
 if (typeof document === 'undefined') return () => {}

 let decorateTimer: number | null = null
 const scheduleDecorate = () => {
 if (decorateTimer != null) return
 decorateTimer = window.setTimeout(() => {
 decorateTimer = null
 decorateRequiredLabels(document)
 refreshInvalidFields(document)
 }, 120)
 }

 const handleInvalid = (event: Event) => {
 const control = event.target instanceof HTMLElement ? event.target : null
 if (!control) return
 const root = nearestFieldRoot(control)
 const label = labelForControl(control)
 const candidate: FieldCandidate = { control, root, label }
 if (root.closest('form')) markScopeAttempted(root.closest('form') as HTMLElement)
 markInvalid(candidate)
 }

 const handleSubmit = (event: Event) => {
 const form = event.target instanceof HTMLFormElement ? event.target : null
 if (!form) return
 markScopeAttempted(form)
 const invalid = validateScope(form)
 if (invalid.length > 0) {
 stopValidationEvent(event)
 focusFirstInvalid(invalid)
 }
 }

 const handleClick = (event: MouseEvent) => {
 const target = event.target instanceof Element ? event.target : null
 const button = target?.closest<HTMLElement>('button, input[type="submit"], [role="button"]')
 if (!button || !isValidationAction(button)) return
 const scope = validationScopeForButton(button)
 if (!scope) return
 markScopeAttempted(scope)
 const invalid = validateScope(scope)
 if (invalid.length > 0) {
 stopValidationEvent(event)
 focusFirstInvalid(invalid)
 }
 }

 const handleChange = (event: Event) => {
 const target = event.target instanceof HTMLElement ? event.target : null
 if (!target) return
 const root = nearestFieldRoot(target)
 if (root.getAttribute(FIELD_ATTR) === 'true' || root.closest(`[${ATTEMPTED_ATTR}="true"]`)) {
 refreshInvalidFields(root)
 }
 }

 const handlePostClick = () => {
 window.setTimeout(() => refreshInvalidFields(document), 0)
 }

 const observer = new MutationObserver(scheduleDecorate)
 observer.observe(document.body, { childList: true, subtree: true })

 decorateRequiredLabels(document)

 document.addEventListener('invalid', handleInvalid, true)
 document.addEventListener('submit', handleSubmit, true)
 document.addEventListener('click', handleClick, true)
 document.addEventListener('click', handlePostClick)
 document.addEventListener('input', handleChange, true)
 document.addEventListener('change', handleChange, true)

 return () => {
 if (decorateTimer != null) window.clearTimeout(decorateTimer)
 observer.disconnect()
 document.removeEventListener('invalid', handleInvalid, true)
 document.removeEventListener('submit', handleSubmit, true)
 document.removeEventListener('click', handleClick, true)
 document.removeEventListener('click', handlePostClick)
 document.removeEventListener('input', handleChange, true)
 document.removeEventListener('change', handleChange, true)
 }
}
